"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.canonicalizeBoundaryDefinition = canonicalizeBoundaryDefinition;
exports.canonicalizeModeContent = canonicalizeModeContent;
exports.canonicalizeModeConditions = canonicalizeModeConditions;
exports.canonicalizeModeRefById = canonicalizeModeRefById;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const generate_readable_id_1 = require("../../GenerateReadableId/generate-readable-id");
const signal_name_normalization_1 = require("../../DataScience/signal-hygiene/signal-name-normalization");
const CONDITION_ID_SEPARATOR = ',';
const SIGNAL_STATE_SUFFIX = '_state';
var UnfoldableMode;
(function (UnfoldableMode) {
    UnfoldableMode["Absent"] = "absent_mode";
    UnfoldableMode["Cyclic"] = "cyclic_mode";
    UnfoldableMode["Contentless"] = "contentless_mode";
})(UnfoldableMode || (UnfoldableMode = {}));
function unfoldableModeId(reason, modeId) {
    return sanitizeConditionId(`<${reason}:${modeId}>`);
}
function sanitizeConditionId(id, logger) {
    if (!id.includes(CONDITION_ID_SEPARATOR)) {
        return id;
    }
    logger?.warn('SanitizeConditionId_SeparatorInConditionId', { conditionId: id });
    return id.split(CONDITION_ID_SEPARATOR).join('_');
}
function resolveTargetNames(signals, target) {
    if (target === undefined) {
        return [];
    }
    const nameById = new Map(signals.map((signal) => [String(signal.id), signal.name]));
    return (0, sigma_engine_shared_1.getBoundaryTargetSignalIds)(signals, target)
        .map((id) => nameById.get(String(id)) ?? String(id))
        .map(signal_name_normalization_1.normalizeSignalNameForComparison)
        .sort();
}
function boundsOf(numerical) {
    const nominal = numerical.nominalValue ?? null;
    switch (numerical.type) {
        case 'range':
            return { min: numerical.minVal, nominal, max: numerical.maxVal };
        case 'minimum':
            return { min: numerical.value, nominal, max: null };
        case 'maximum':
            return { min: null, nominal, max: numerical.value };
        case 'absolute':
            return { min: null, nominal: numerical.value, max: null };
    }
}
function canonicalizeBoundaryDefinition(definition, signals, logger) {
    const targetName = resolveTargetNames(signals, definition.target).join('+');
    if (!(0, sigma_engine_shared_1.isBoundaryNumericalDefinition)(definition)) {
        return sanitizeConditionId(`${targetName}${SIGNAL_STATE_SUFFIX}`, logger);
    }
    const { min, nominal, max } = boundsOf(definition.numerical);
    if (min === null && nominal === null && max === null) {
        return sanitizeConditionId(`${targetName}${SIGNAL_STATE_SUFFIX}`, logger);
    }
    return sanitizeConditionId((0, generate_readable_id_1.generateReadableConditionModeId)(targetName, min, nominal, max), logger);
}
function canonicalizeModeContent(mode, modes, signals, seen = new Set(), logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
    const nextSeen = new Set(seen).add(mode.id);
    const preconditions = mode.preconditions ?? [];
    if (preconditions.length === 0) {
        logger.warn('CanonicalizeModeContent_NoPreconditions', { modeId: mode.id });
        return unfoldableModeId(UnfoldableMode.Contentless, mode.id);
    }
    if (preconditions.length > 1) {
        logger.warn('CanonicalizeModeContent_MultiplePreconditions', {
            modeId: mode.id,
            preconditionCount: preconditions.length,
        });
    }
    const ids = preconditions.map((precondition) => canonicalizeCondition(precondition.condition, modes, signals, nextSeen, logger));
    return [...new Set(ids)].sort().join(CONDITION_ID_SEPARATOR);
}
function canonicalizeCondition(condition, modes, signals, seen, logger) {
    if ((0, sigma_engine_shared_1.isModeReferenceCondition)(condition)) {
        if (seen.has(condition.mode)) {
            logger.warn('CanonicalizeCondition_CyclicModeReference', { modeId: condition.mode });
            return unfoldableModeId(UnfoldableMode.Cyclic, condition.mode);
        }
        const referenced = modes.find((mode) => mode.id === condition.mode);
        if (referenced === undefined) {
            logger.warn('CanonicalizeCondition_AbsentModeReference', { modeId: condition.mode });
            return unfoldableModeId(UnfoldableMode.Absent, condition.mode);
        }
        return canonicalizeModeContent(referenced, modes, signals, seen, logger);
    }
    if ((0, sigma_engine_shared_1.isModeSignalLogicalStateCondition)(condition)) {
        const signal = signals.find((candidate) => String(candidate.id) === String(condition.signalId));
        const name = (0, signal_name_normalization_1.normalizeSignalNameForComparison)(signal?.name ?? String(condition.signalId));
        return sanitizeConditionId(`${name}${SIGNAL_STATE_SUFFIX}`, logger);
    }
    return canonicalizeBoundaryDefinition(condition.boundary, signals, logger);
}
function canonicalizeModeConditions(conditions, modes, signals, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
    if (conditions === undefined || conditions.length === 0) {
        return '';
    }
    const ids = conditions.map((condition) => canonicalizeCondition(condition, modes, signals, new Set(), logger));
    return [...new Set(ids)].sort().join(CONDITION_ID_SEPARATOR);
}
function canonicalizeModeRefById(modeId, modes, signals, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
    if (modeId === null || modeId === undefined || modeId === '') {
        return '';
    }
    const mode = modes.find((candidate) => candidate.id === modeId);
    if (mode === undefined) {
        logger.warn('CanonicalizeModeRefById_AbsentMode', { modeId });
        return unfoldableModeId(UnfoldableMode.Absent, modeId);
    }
    return canonicalizeModeContent(mode, modes, signals, new Set(), logger);
}
//# sourceMappingURL=condition-canonicalization.js.map